'use client';

import React, { useMemo, useState } from 'react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { normalize } from '@/app/services/degreeRequirementService';
import { usePlanner } from '../plannerContext';
import { formatCode, requirementsForCourse } from '../plannerModel';
import { ProgramDot, StatusPill } from '../PlannerPrimitives';

type SortKey = 'term' | 'code' | 'title' | 'credits' | 'status';

interface Row {
  key: string;
  order: number;
  termName: string;
  code: string | null;
  title: string;
  credits: number;
  status: 'earned' | 'planned' | 'placeholder' | 'issue';
  counts: ReturnType<typeof requirementsForCourse>;
  open: (el: HTMLElement) => void;
}

export default function ListView() {
  const { terms, completed, programs, issuesByCode, nameOf, creditsOf, listLabel, listCredits, open } = usePlanner();
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'term', dir: 1 });
  const [showEarned, setShowEarned] = useState(false);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    if (showEarned) {
      completed.forEach((code) => out.push({
        key: `e-${code}`, order: -1, termName: 'Earned', code, title: nameOf(code), credits: creditsOf(code), status: 'earned',
        counts: requirementsForCourse(code, programs), open: (el) => open({ kind: 'course', code, anchor: el }),
      }));
    }
    terms.forEach((term, order) => term.items.forEach(({ item, index }) => {
      if (item.kind === 'course_list' && !item.selectedCourseCode) {
        out.push({
          key: `${term.number}-${index}`, order, termName: term.name, code: null, title: item.label || listLabel(item.mappingName),
          credits: item.credits || listCredits(item.mappingName), status: 'placeholder',
          counts: programs.flatMap((p) => p.requirements.filter((r) => r.listNames.includes(item.mappingName))),
          open: (el) => open({ kind: 'list', semesterNumber: term.number, index, anchor: el }),
        });
        return;
      }
      const code = normalize(item.kind === 'course_list' ? item.selectedCourseCode || '' : item.code);
      out.push({
        key: `${term.number}-${index}`, order, termName: term.name, code, title: nameOf(code),
        credits: (item.kind === 'course' ? Number(item.credits) : 0) || creditsOf(code),
        status: issuesByCode.has(code) ? 'issue' : 'planned',
        counts: requirementsForCourse(code, programs),
        open: (el) => item.kind === 'course_list' ? open({ kind: 'list', semesterNumber: term.number, index, anchor: el }) : open({ kind: 'course', code, anchor: el }),
      });
    }));
    const value = (row: Row): string | number => {
      switch (sort.key) {
        case 'term': return row.order * 1000 + (row.code ? Number(row.code.slice(-3)) : 999);
        case 'code': return row.code || 'zzz';
        case 'title': return row.title.toLowerCase();
        case 'credits': return row.credits;
        default: return row.status;
      }
    };
    return out.sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
    });
  }, [terms, completed, programs, issuesByCode, nameOf, creditsOf, listLabel, listCredits, open, sort, showEarned]);

  const header = (key: SortKey, label: string, className = '') => (
    <th scope="col" className={`px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--ss-text-muted)] ${className}`} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="inline-flex items-center gap-1 hover:text-[var(--ss-text)]" onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))}>
        {label}<ChevronUpDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </th>
  );

  const total = rows.reduce((sum, row) => sum + (row.credits || 0), 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[var(--ss-text-muted)]"><b className="text-[var(--ss-text)]">{rows.length}</b> rows · <b className="text-[var(--ss-text)]">{total}</b> credits</p>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--ss-text-muted)]">
          <input type="checkbox" checked={showEarned} onChange={(e) => setShowEarned(e.target.checked)} className="h-4 w-4 accent-[var(--ss-brand)]" />
          Include earned courses
        </label>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[var(--ss-border)] bg-[var(--ss-surface)]">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead className="bg-[var(--ss-surface-subtle)]">
            <tr>
              {header('term', 'Term')}
              {header('code', 'Course')}
              {header('title', 'Title')}
              {header('credits', 'Cr', 'w-14')}
              <th scope="col" className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--ss-text-muted)]">Counts toward</th>
              {header('status', 'Status')}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-[var(--ss-border)] hover:bg-[var(--ss-surface-subtle)]">
                <td className="whitespace-nowrap px-3 py-2 text-xs text-[var(--ss-text-muted)]">{row.termName}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{row.code ? formatCode(row.code) : '—'}</td>
                <td className="px-3 py-2">
                  <button type="button" onClick={(e) => row.open(e.currentTarget)} className={`text-left font-semibold hover:text-[var(--ss-brand)] hover:underline ${row.status === 'placeholder' ? 'italic' : ''}`}>{row.title}</button>
                </td>
                <td className="px-3 py-2 tabular-nums">{row.credits || '?'}</td>
                <td className="px-3 py-2">
                  <span className="flex flex-col gap-0.5">
                    {row.counts.slice(0, 2).map((req) => <span key={req.key} className="flex items-center gap-1.5 text-xs"><ProgramDot index={req.programIndex} /><span className="truncate">{req.title}</span></span>)}
                    {!row.counts.length ? <span className="text-xs text-[var(--ss-text-muted)]">—</span> : null}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {row.status === 'earned' ? <StatusPill tone="earned">Earned</StatusPill>
                    : row.status === 'issue' ? <StatusPill tone="missing">Issue</StatusPill>
                      : row.status === 'placeholder' ? <StatusPill tone="neutral">Choose later</StatusPill>
                        : <StatusPill tone="planned">Planned</StatusPill>}
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-[var(--ss-text-muted)]">Nothing planned yet.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
